import { useCallback, useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";

const mediaUrl = (url) => (url ? `${process.env.REACT_APP_BACKEND_URL}${url}` : null);

function BannerImage({ banner }) {
  const img = (
    <img
      key={banner.image_url}
      src={mediaUrl(banner.image_url)}
      alt={banner.title || "Banner"}
      className="block w-full animate-in fade-in duration-500"
      data-testid="catalog-hero-image"
    />
  );
  if (!banner.cta_link) return img;
  const external = /^https?:\/\//i.test(banner.cta_link);
  return (
    <a
      href={banner.cta_link}
      target={external ? "_blank" : undefined}
      rel={external ? "noreferrer" : undefined}
      className="block"
      data-testid="catalog-hero-link"
    >
      {img}
    </a>
  );
}

export function BannerCarousel({ banners = [], fallbackTitle = "", fallbackSubtitle = "", hasKits = false }) {
  const [index, setIndex] = useState(0);
  const count = banners.length;

  const go = useCallback((next) => setIndex((i) => (next + count) % count), [count]);

  useEffect(() => {
    if (count < 2) return undefined;
    const t = setInterval(() => setIndex((i) => (i + 1) % count), 7000);
    return () => clearInterval(t);
  }, [count]);

  if (count === 0) return null;
  const b = banners[Math.min(index, count - 1)];

  return (
    <section className="relative mb-10" data-testid="catalog-hero-carousel">
      <div
        className={`tr-card-raised relative overflow-hidden ${
          b.image_url ? "" : "px-6 py-10 md:px-10 md:py-14"
        }`}
        data-testid="catalog-hero"
      >
        {b.image_url ? (
          // Banner com imagem é peça gráfica independente: nada é sobreposto.
          <BannerImage banner={b} />
        ) : (
          <>
            <div
              className="pointer-events-none absolute inset-0 opacity-90"
              style={{
                background:
                  "radial-gradient(circle at 82% 12%, hsl(var(--brand) / 0.22), transparent 55%), radial-gradient(circle at 0% 100%, hsl(var(--primary) / 0.14), transparent 45%)",
              }}
            />
            <div key={b.id} className="relative max-w-2xl animate-in fade-in duration-500">
              <span className="tr-status tr-status-info" data-testid="hero-badge">
                <Sparkles className="h-3 w-3" /> {b.badge || "Feito na hora, na medida"}
              </span>
              <h1 className="mt-4 text-4xl font-semibold sm:text-5xl" data-testid="hero-title">
                {b.title || fallbackTitle || (
                  <>
                    Presentes que saem do <span className="text-brand">comum</span>.
                  </>
                )}
              </h1>
              <p className="mt-4 max-w-xl text-base text-muted-foreground" data-testid="hero-subtitle">
                {b.subtitle ||
                  fallbackSubtitle ||
                  "Produtos criativos feitos em impressão 3D. Monte seu pedido e finalize pelo WhatsApp."}
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Button asChild className="h-11 rounded-full px-6 font-semibold" data-testid="hero-cta-btn">
                  <a href={b.cta_link || "#produtos"}>{b.cta_label || "Ver produtos"}</a>
                </Button>
                {(b.cta2_label || (!b.cta_label && hasKits)) && (
                  <Button
                    asChild
                    variant="soft"
                    className="h-11 rounded-full px-6 font-semibold"
                    data-testid="hero-kits-btn"
                  >
                    <a href={b.cta2_link || "#kits"}>{b.cta2_label || "Ver kits"}</a>
                  </Button>
                )}
              </div>
            </div>
          </>
        )}

        {count > 1 && (
          <>
            <button
              type="button"
              onClick={() => go(index - 1)}
              aria-label="Banner anterior"
              data-testid="hero-prev-btn"
              className="absolute left-2 top-1/2 hidden -translate-y-1/2 rounded-full border border-white/10 bg-background/60 p-2 text-muted-foreground backdrop-blur transition-colors hover:text-foreground md:block"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button
              type="button"
              onClick={() => go(index + 1)}
              aria-label="Próximo banner"
              data-testid="hero-next-btn"
              className="absolute right-2 top-1/2 hidden -translate-y-1/2 rounded-full border border-white/10 bg-background/60 p-2 text-muted-foreground backdrop-blur transition-colors hover:text-foreground md:block"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </>
        )}
      </div>

      {count > 1 && (
        <div className="mt-4 flex justify-center gap-2" data-testid="hero-dots">
          {banners.map((item, i) => (
            <button
              key={item.id || i}
              type="button"
              onClick={() => setIndex(i)}
              aria-label={`Ir para o banner ${i + 1}`}
              data-testid={`hero-dot-${i}`}
              className={`h-2 rounded-full transition-all duration-300 ${
                i === index ? "w-8 bg-brand" : "w-2 bg-white/25 hover:bg-white/40"
              }`}
            />
          ))}
        </div>
      )}
    </section>
  );
}
